import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Client } from '@hero/schema';
import { useEditClientLegalManager } from '@hero/web-hooks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Input } from '../../../ui/Input';
import { Label } from '../../../ui/Label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';
import {
  FinancingFieldBadge,
  type FinancingFieldBadgeVariant,
} from '../../../app/FinancingFieldBadge';

const ACCEPTED_DOCUMENT_TYPES: Record<string, string[]> = {
  'application/pdf': [],
  'image/jpeg': [],
  'image/png': [],
};

/**
 * Where this drawer is being rendered. Controls only the visual
 * decoration of fields — `required` vs `recommended` chips — not
 * the underlying save validation (the manager hook stays
 * permissive in both modes; hard gates live at the consumer's
 * completeness layer, see e.g. `Financing/components/Requirements`).
 *
 * Two contexts:
 *
 * - `'crm'`         — CRM workspace (Client page edit sheet).
 *                     Every field is optional. A few are tagged
 *                     `recommended` so a carrier filling in a
 *                     Client now sees which fields they'd want
 *                     ready if they later route that Client
 *                     through financing.
 * - `'financing'`   — Inside the financing apply flow's
 *                     Customer-legal requirement drawer. CR id +
 *                     CR name + Tax id are hard-required for the
 *                     lender to underwrite; everything else is
 *                     still optional but `recommended`.
 */
export type ClientLegalDisplayFor = 'crm' | 'financing';

export interface ClientLegalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The client being edited. The drawer reads `client.legal` to
   * hydrate the form and writes back via `useClientUpdateApi`.
   * Undefined → drawer is inert; we still mount it so the close
   * animation can play (matches the surrounding shadcn `Sheet`
   * lifecycle).
   */
  client: Client | undefined;
  /**
   * Visual context (see {@link ClientLegalDisplayFor}). Defaults
   * to `'crm'` — i.e. callers from the workspace get the relaxed
   * "everything optional" treatment unless they opt in.
   */
  displayFor?: ClientLegalDisplayFor;
  /** Fires after a successful save. */
  onSaved?: () => void;
}

/**
 * `ClientLegalDrawer` — Drawer-shell editor for a client's legal
 * block (registration / tax / optional transport license / address).
 *
 * Used in two contexts that share the exact same field set but
 * differ in which fields they hard-care about:
 *  1. **CRM workspace** — the carrier is just keeping their book
 *     of clients clean. Nothing is required.
 *  2. **Financing apply flow** — the lender needs CR id + CR name
 *     + Tax id to underwrite. Other fields are still optional but
 *     "recommended" (better-priced offers / faster approvals).
 *
 * Rather than fork the form, the drawer accepts a `displayFor`
 * prop that decides which {@link FinancingFieldBadge} renders next
 * to each label. The save action stays permissive in both modes —
 * hard gating happens *outside* the drawer (e.g. the financing
 * `RequirementsCard` blocks submit until the missing fields land,
 * regardless of how the user saved).
 *
 * Layout intentionally single-column / full-width — Riyadh-style
 * tax ids and CR names easily overflow the half-width slot the
 * previous 2-col layout left for them, and the financing
 * requirements drawer pattern (carrier legal, banking, agreement)
 * is also single-column for consistency.
 *
 * The license section is gated behind a `hasLicense` checkbox.
 * Most CRM clients are end-customers without a transport license,
 * so the heavy uploader UI stays collapsed by default. Toggling
 * off scrubs the license-related fields on save.
 */
export function ClientLegalDrawer({
  isOpen,
  onClose,
  client,
  displayFor = 'crm',
  onSaved,
}: ClientLegalDrawerProps) {
  const { t } = useTranslation(['m-client', 'c-main']);
  const {
    draft,
    setField,
    setImageFile,
    registrationFiles,
    taxFiles,
    licenseFiles,
    registrationExistingUrls,
    taxExistingUrls,
    licenseExistingUrls,
    onSave,
    isPending,
    isSuccess,
    reset,
  } = useEditClientLegalManager(client);

  // Reset draft state on close so re-opening doesn't surface stale
  // edits or a stuck `isSuccess` flag. Single dependency keeps the
  // effect predictable — re-running on `reset`'s identity change
  // would clobber in-progress edits.
  useEffect(() => {
    if (!isOpen) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-close after a successful save. We watch the underlying
  // mutation's `isSuccess` rather than awaiting `onSave` directly
  // so a retry-loop after a failed save doesn't blow up the drawer
  // mid-flight.
  useEffect(() => {
    if (isSuccess && isOpen) {
      onSaved?.();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isOpen]);

  const handleSave = async () => {
    try {
      await onSave();
    } catch (err) {
      // The host app's global mutation handler surfaces a toast
      // with the SDK error message; we just keep the drawer open so
      // the user can fix and retry.
      console.error('Failed to update client legal:', err);
    }
  };

  /**
   * Resolve the financing chip variant for a field based on the
   * current `displayFor`. The {@link CHIP_MAP} below is the single
   * source of truth for "which fields the financing flow cares
   * about, and at what severity."
   */
  const chipFor = (field: keyof typeof CHIP_MAP): FinancingFieldBadgeVariant | null => {
    const entry = CHIP_MAP[field];
    return entry[displayFor] ?? null;
  };

  /**
   * Whether the field currently carries a value — drives the chip's
   * "Provided for financing" green state. Centralised here so each
   * `<Field>` call below stays a one-liner.
   *
   * Text fields → non-empty trimmed string.
   * Image fields → freshly picked file OR existing remote URL.
   */
  const isString = (v: unknown): v is string =>
    typeof v === 'string' && v.trim().length > 0;
  const satisfiedFor = (field: keyof typeof CHIP_MAP): boolean => {
    switch (field) {
      case 'registrationName':
        return isString(draft.registrationName);
      case 'registrationId':
        return isString(draft.registrationId);
      case 'registrationImage':
        return registrationFiles.length > 0 || registrationExistingUrls.length > 0;
      case 'taxId':
        return isString(draft.taxId);
      case 'taxImage':
        return taxFiles.length > 0 || taxExistingUrls.length > 0;
      case 'licenseId':
        return isString(draft.licenseId);
      case 'licenseImage':
        return licenseFiles.length > 0 || licenseExistingUrls.length > 0;
      case 'fullAddress':
        return isString(draft.fullAddress);
      case 'addressCity':
        return isString(draft.addressCity);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle>{t('m-client:edit_modal.legal.title')}</SheetTitle>
          <SheetDescription>
            {displayFor === 'financing'
              ? t('m-client:edit_modal.legal.description_financing', {
                  defaultValue: t('m-client:edit_modal.legal.description'),
                })
              : t('m-client:edit_modal.legal.description')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 p-6">
          <FormSection title={t('m-client:edit_modal.legal.section.registration')}>
            {/* Order: legal name → CR number → document. Matches
                the carrier-side drawer for visual consistency and
                reads more naturally — the lender's first match is
                against the legal name (e.g. Wathiq lookups), not
                the numeric id. */}
            <Field
              label={t('m-client:edit_modal.field.registration_name.title')}
              chip={chipFor('registrationName')}
              satisfied={satisfiedFor('registrationName')}>
              <Input
                value={draft.registrationName ?? ''}
                placeholder={t('m-client:edit_modal.field.registration_name.placeholder')}
                onChange={e => setField('registrationName', e.target.value)}
              />
            </Field>
            <Field
              label={t('m-client:edit_modal.field.registration_id.title')}
              chip={chipFor('registrationId')}
              satisfied={satisfiedFor('registrationId')}>
              <Input
                value={draft.registrationId ?? ''}
                placeholder={t('m-client:edit_modal.field.registration_id.placeholder')}
                onChange={e => setField('registrationId', e.target.value)}
              />
            </Field>
            <Field
              label={t('m-client:edit_modal.field.registration_image.title')}
              chip={chipFor('registrationImage')}
              satisfied={satisfiedFor('registrationImage')}>
              <FileUploader
                files={registrationFiles}
                onChange={files => setImageFile('registrationImage', files)}
                acceptedFileTypes={ACCEPTED_DOCUMENT_TYPES}
                label={t('m-client:edit_modal.field.registration_image.placeholder')}
                existingUrls={registrationExistingUrls}
              />
            </Field>
          </FormSection>

          <FormSection title={t('m-client:edit_modal.legal.section.tax')}>
            <Field
              label={t('m-client:edit_modal.field.tax_id.title')}
              chip={chipFor('taxId')}
              satisfied={satisfiedFor('taxId')}>
              <Input
                value={draft.taxId ?? ''}
                placeholder={t('m-client:edit_modal.field.tax_id.placeholder')}
                onChange={e => setField('taxId', e.target.value)}
              />
            </Field>
            <Field
              label={t('m-client:edit_modal.field.tax_image.title')}
              chip={chipFor('taxImage')}
              satisfied={satisfiedFor('taxImage')}>
              <FileUploader
                files={taxFiles}
                onChange={files => setImageFile('taxImage', files)}
                acceptedFileTypes={ACCEPTED_DOCUMENT_TYPES}
                label={t('m-client:edit_modal.field.tax_image.placeholder')}
                existingUrls={taxExistingUrls}
              />
            </Field>
          </FormSection>

          <FormSection title={t('m-client:edit_modal.legal.section.license')}>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2.5">
              <Checkbox
                checked={!!draft.hasLicense}
                onCheckedChange={next => setField('hasLicense', next === true)}
                className="mt-0.5"
              />
              <span className="space-y-0.5">
                <span className="block text-sm font-medium text-gray-900">
                  {t('m-client:edit_modal.legal.has_license.title')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('m-client:edit_modal.legal.has_license.description')}
                </span>
              </span>
            </label>

            {draft.hasLicense ? (
              <div className="mt-3 space-y-4">
                <Field
                  label={t('m-client:edit_modal.field.license_id.title')}
                  chip={chipFor('licenseId')}
                  satisfied={satisfiedFor('licenseId')}>
                  <Input
                    value={draft.licenseId ?? ''}
                    placeholder={t('m-client:edit_modal.field.license_id.placeholder')}
                    onChange={e => setField('licenseId', e.target.value)}
                  />
                </Field>
                <Field
                  label={t('m-client:edit_modal.field.license_image.title')}
                  chip={chipFor('licenseImage')}
                  satisfied={satisfiedFor('licenseImage')}>
                  <FileUploader
                    files={licenseFiles}
                    onChange={files => setImageFile('licenseImage', files)}
                    acceptedFileTypes={ACCEPTED_DOCUMENT_TYPES}
                    label={t('m-client:edit_modal.field.license_image.placeholder')}
                    existingUrls={licenseExistingUrls}
                  />
                </Field>
              </div>
            ) : null}
          </FormSection>

          <FormSection title={t('m-client:edit_modal.legal.section.address')}>
            <Field
              label={t('m-client:edit_modal.field.full_address.title')}
              chip={chipFor('fullAddress')}
              satisfied={satisfiedFor('fullAddress')}>
              <Input
                value={draft.fullAddress ?? ''}
                placeholder={t('m-client:edit_modal.field.full_address.placeholder')}
                onChange={e => setField('fullAddress', e.target.value)}
              />
            </Field>
            <Field
              label={t('m-client:edit_modal.field.address_city.title')}
              chip={chipFor('addressCity')}
              satisfied={satisfiedFor('addressCity')}>
              <Input
                value={draft.addressCity ?? ''}
                placeholder={t('m-client:edit_modal.field.address_city.placeholder')}
                onChange={e => setField('addressCity', e.target.value)}
              />
            </Field>
          </FormSection>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-white px-6 py-3">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {t('c-main:crud_buttons.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isPending || !client}>
            {isPending
              ? t('c-main:async_operations.saving')
              : t('c-main:crud_buttons.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------------------------------------------------- */
/* Field → financing badge mapping                                */
/* -------------------------------------------------------------- */

/**
 * Per-field, per-context chip recipe. `null` means "no chip".
 *
 * In `'financing'` we mark CR Name / CR Number / Tax id as
 * `required` (the lender hard-requires them) and everything else
 * as `recommended` (improves approval odds / pricing).
 *
 * In `'crm'` we still want the user to see *which* fields the
 * financing flow would want — so the carrier filling a CRM
 * client now knows what they'd be missing if they later route the
 * same client through financing. All chips in CRM are
 * `recommended` (CRM is permissive by design).
 */
const CHIP_MAP: Record<
  | 'registrationId'
  | 'registrationName'
  | 'registrationImage'
  | 'taxId'
  | 'taxImage'
  | 'licenseId'
  | 'licenseImage'
  | 'fullAddress'
  | 'addressCity',
  Record<ClientLegalDisplayFor, FinancingFieldBadgeVariant | null>
> = {
  registrationId: { financing: 'required', crm: 'recommended' },
  registrationName: { financing: 'required', crm: 'recommended' },
  registrationImage: { financing: 'recommended', crm: 'recommended' },
  taxId: { financing: 'required', crm: 'recommended' },
  taxImage: { financing: 'recommended', crm: 'recommended' },
  licenseId: { financing: 'recommended', crm: null },
  licenseImage: { financing: 'recommended', crm: null },
  fullAddress: { financing: 'recommended', crm: null },
  addressCity: { financing: 'recommended', crm: null },
};

/* -------------------------------------------------------------- */
/* Internal helpers                                                */
/* -------------------------------------------------------------- */

function FormSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  children,
  chip,
  satisfied,
}: {
  label: string;
  children: React.ReactNode;
  chip?: FinancingFieldBadgeVariant | null;
  /** Forwarded to {@link FinancingFieldBadge} — true → green chip. */
  satisfied?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
        {chip ? <FinancingFieldBadge variant={chip} satisfied={satisfied} /> : null}
      </div>
      {children}
    </div>
  );
}
